import React, { useEffect, useState } from 'react'
import { Button, Form, Alert, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import '../styles/Campaigns.css'
import CustomDataGrid from '../components/CustomDataGrid'
import {
  campaignGridColumns,
  generateGridColumns,
  generateGridRows,
  campaignRowMapping,
  campaignMapping,
  generateFormData,
  validateUrl,
  formatUtcToLocal
} from '../utils/PageUtils'
import { createCampaign, getCampaigns } from '../utils/api'
import PageHelmet from '../components/PageHelmet'
import PropTypes from 'prop-types'
import { IconButton } from "@mui/material";
import { MdArrowBackIos } from "react-icons/md";

function Campaigns({ userRole, bearerToken }) {
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaign, setSelectedCampaign] = useState(null)

  const [campaignName, setCampaignName] = useState('')
  const [destinationUrl, setDestinationUrl] = useState('')
  const [error, setError] = useState(null)
  const [info, setInfo] = useState('')
  const [refreshGrid, setRefreshGrid] = useState(true)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [openCreateCampaignModal, setOpenCreateCampaignModal] = useState(false)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!bearerToken) {
        console.warn('No access token available')
        return
      }
      if (campaigns.length === 0) {
        setIsLoading(true)
      }
      try {
        const data = await getCampaigns(bearerToken)
        if (data?.records) {
          setCampaigns(data.records)
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error)
      } finally {
        setIsLoading(false)
        if (refreshGrid) {
          setRefreshGrid(false)
        }
      }
    }
    if (refreshGrid) {
      loadCampaigns()
    }
  }, [refreshGrid])

  const handleInputChange = (setter) => (e) => setter(e.target.value)

  const handleRowClick = (e) => {

    if (selectedRowId === e.id) {
      setSelectedRowId(null)
      setSelectedCampaign(null)
    } else {
      const selected = campaigns[e.id - 1]
      setSelectedCampaign(selected)
      setSelectedRowId(e.id)
    }
  }

  const handleSubmit = async (campaignName, destinationUrl) => {
    setError(null)
    setInfo(null)
    if (!campaignName.trim()) {
      setError("Please enter a campaign name")
      return
    }
    if (!destinationUrl.trim()) {
      setError("Please enter a destination URL")
      return
    }
    const trimmedCampaignName = campaignName.trim()
    const trimmedDestinationUrl = destinationUrl.trim()

    if (!validateUrl(trimmedDestinationUrl)) {
      setError(
        'Please enter a valid URL. Required format: https://yourcompany.com/landingPageX',
      )
      return
    }

    const payload = {
      name: trimmedCampaignName,
      destination_url: trimmedDestinationUrl,
    }

    try {
      const response = await createCampaign(payload, bearerToken)

      switch (response.status) {
        case 201:
          setInfo(`The campaign "${payload.name}" was created successfully.`)
          setCampaignName('')
          setDestinationUrl('')
          setRefreshGrid(true)
          break
        case 409:
          setError(`A campaign "${payload.name}" already exists for user.`)
          break
        default:
          setError(
            'The operation you attempted has failed. Please contact support.',
          )
      }
    } catch (error) {
      setError('The operation has failed. Please contact support.')
    }
  }
  const handleCloseCampaignDetails = () => {
    setSelectedCampaign(null);
    setSelectedRowId(null);
  }

  const renderCampaignDetails = () => (
    <div className="campaign-details-card">
      <div className="details-header-area" >
        <p className="details-title-top">CAMPAIGN DETAILS</p>
      </div>
      <div className="details-body-content">
        <div className="details-content-box">
          {[
            { label: "Campaign Name", value: selectedCampaign.campaign_name },
            { label: "Total Contacts", value: selectedCampaign.total },
            { label: "Destination URL", value: selectedCampaign.destination_url },
            { label: "Created Date", value: formatUtcToLocal(selectedCampaign.created) },
          ].map((field, idx) => (
            <div className="details-row-item" key={idx}>
              <p className="details-label-text">{field.label}:</p>
              <p className="details-value-text">{field.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null

  const campaignColumns = generateGridColumns(
    campaignGridColumns,
    'campaign_name',
  )

  const campaignRows = generateGridRows(campaigns, campaignRowMapping)

  if (isLoading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" role="status" />
        <p>Please wait...</p>
      </div>
    )
  }
  const handleOpenCreateModal = () => {
    setOpenCreateCampaignModal(true)
    setError(null)
    setInfo(null)
    setCampaignName('')
    setDestinationUrl('')
  }
  return userRole === 'customer' ? (
    <>
      <PageHelmet
        title="Create Campaign"
        description="Create a new campaign with TraQR."
        url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/create_campaign`}
      />
      <div className={`campaign-container ${selectedCampaign ? 'has-selection' : ''}`}>
        <div className="campaign-header-container">
          <h1 className="main-heading heading-color"> Campaigns</h1>
        </div>

        {campaigns.length === 0 ? (
          <div className="no-campaigns-container">
            <Alert variant="primary">
              You do not have any existing campaigns
            </Alert>
          </div>
        ) : (
          <div
            className={`campaign-layout-wrapper ${selectedCampaign ? 'layout-split' : 'layout-centered'
              } `}
          >
            <div className="campaign-grid-section">
              <CustomDataGrid
                rows={campaignRows}
                columns={campaignColumns}
                totalRows={campaigns.length}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowId}
                toolbarProps={{
                  createAction: {
                    show: true,
                    onClick: handleOpenCreateModal,
                    label: "+ Create"
                  }
                }}
              />
            </div>
            {!isMobile && selectedCampaign && (
              <div className="campaign-details-section">
                {renderCampaignDetails()}
              </div>
            )}

            {isMobile && selectedCampaign && (
              <div className="campaign-mobile-overlay mobile-open">
                <div className="mobile-header">
                  <IconButton size="small" style={{ color: '#fff' }} onClick={handleCloseCampaignDetails}>
                    <MdArrowBackIos />
                  </IconButton>
                  <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>Campaign Details</span>
                </div>
                <div className="mobile-body">
                  {renderCampaignDetails()}
                </div>
              </div>
            )}
          </div>
        )}

        <Modal
          show={openCreateCampaignModal}
          onHide={() => setOpenCreateCampaignModal(false)}
          centered
          size="lg"
          backdrop="static"
          dialogClassName="create-campaign-modal"
        >
          <Modal.Header>
            <Modal.Title>Create Campaign</Modal.Title>
          </Modal.Header>

          <Modal.Body className="px-4 py-3">
            <Alert
              variant="warning"
              className="py-2 mb-4"
            >
              Campaign name and destination URL cannot be modified after creation
            </Alert>
            <Form className="campaign-form">

              <div className="row g-4">
                {/* Campaign Name */}
                <div className="col-12">
                  <Form.Group controlId="campaignName">
                    <Form.Label className="modal-input-label d-flex justify-content-between align-items-center w-100">
                      <span>
                        Campaign Name <span className="text-danger">*</span>
                      </span>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="campaign-name-tooltip" style={{ zIndex: 20000 }}>
                            Campaign name must be unique.
                          </Tooltip>
                        }
                      >
                        <span className="d-inline-block">
                          <IconButton size="small">
                            <HelpOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      className="modal-form-control"
                      type="text"
                      placeholder="Eg: Postcard-ForHayward"
                      value={campaignName}
                      onChange={handleInputChange(setCampaignName)}
                    />
                  </Form.Group>
                </div>

                {/* Destination URL */}
                <div className="col-12">
                  <Form.Group controlId="destinationUrl">
                    <Form.Label className="modal-input-label d-flex justify-content-between align-items-center w-100">
                      <span>
                        Destination URL <span className="text-danger">*</span>
                      </span>
                    </Form.Label>
                    <Form.Control
                      className="modal-form-control"
                      type="text"
                      placeholder="https://yourcompany.com/landing-page"
                      value={destinationUrl}
                      onChange={handleInputChange(setDestinationUrl)}
                    />

                  </Form.Group>
                </div>
              </div>

              {/* Alerts */}
              {error && (
                <Alert variant="danger" className="mt-4">
                  {error}
                </Alert>
              )}

              {info && (
                <Alert variant="success" className="mt-4">
                  {info}
                </Alert>
              )}

              {/* Footer */}
              <div className="d-flex justify-content-center gap-4 mt-3 border-top pt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => setOpenCreateCampaignModal(false)}
                >
                  Cancel
                </Button>

                <Button
                  className="create-campaign-button"
                  variant="primary"
                  onClick={() =>
                    handleSubmit(campaignName, destinationUrl)
                  }
                >
                  Create Campaign
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

      </div>
    </>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  )
}

Campaigns.propTypes = {
  userRole: PropTypes.string.isRequired,
  bearerToken: PropTypes.string.isRequired,
}

export default Campaigns
